import React from "react";
import type { CSSProperties } from "react";
import Button from "antd/es/button";
import { appointmentHeaderStyle } from "@openings/template-components";

export interface BookingPageHeaderProps {
  salonName: string;
  salonLogoUrl?: string | null;
  homeHref?: string;
  onInfoClick?: () => void;
  infoButtonRef?: React.RefObject<
    HTMLAnchorElement | HTMLButtonElement | null
  >;
}

/**
 * Shared header for the booking page and subdomain functional pages.
 * Shows the business logo (or name) linked to `/`, with an optional Info button.
 */
export function BookingPageHeader({
  salonName,
  salonLogoUrl,
  homeHref = "/",
  onInfoClick,
  infoButtonRef,
}: BookingPageHeaderProps) {
  const headerLogoUrl = salonLogoUrl ?? null;
  const displayName = salonName?.trim() || "Your studio";
  const textColor = "var(--openings-color-text, #111)";

  return (
    <div
      style={
        {
          ...appointmentHeaderStyle,
          justifyContent: "space-between",
          gap: 16,
          borderBottom: "none",
        } as CSSProperties
      }
    >
      <a
        href={homeHref}
        style={{
          textDecoration: "none",
          color: textColor,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {headerLogoUrl ? (
          <img
            className="openings-booking-brand-logo"
            src={headerLogoUrl}
            alt={`${displayName} logo`}
            style={{
              display: "block",
              height: 28,
              width: "auto",
              maxWidth: "min(40vw, 200px)",
            }}
          />
        ) : (
          <span
            className="openings-booking-brand-name"
            style={{
              fontFamily:
                "var(--openings-booking-heading-font-family, inherit)",
              fontWeight:
                "var(--openings-booking-brand-font-weight, 600)",
              fontSize:
                "var(--openings-booking-brand-font-size, 20px)",
              lineHeight: 1.2,
            }}
          >
            {displayName}
          </span>
        )}
      </a>
      {onInfoClick && (
        <Button
          ref={infoButtonRef}
          type="text"
          onClick={onInfoClick}
          aria-label="Business info"
          icon={
            <svg
              width="15"
              height="15"
              viewBox="0 0 1024 1024"
              fill="currentColor"
            >
              <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
              <path d="M464 336a48 48 0 1096 0 48 48 0 10-96 0zm24 112h48c4.4 0 8 3.6 8 8v272c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V456c0-4.4 3.6-8 8-8z" />
            </svg>
          }
          style={{
            minWidth: 40,
            minHeight: 40,
            border:
              "1px solid var(--openings-booking-header-info-border-color, rgba(17, 17, 17, 0.2))",
            borderRadius: 20,
            color: textColor,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Info
        </Button>
      )}
    </div>
  );
}
